import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import mammoth from "mammoth";

// Set worker for PDF.js v2.0.943 (legacy build)
pdfjsLib.GlobalWorkerOptions.workerSrc =
  "//cdnjs.cloudflare.com/ajax/libs/pdf.js/2.0.943/pdf.worker.min.js";

/**
 * Extract text from PDF file
 * @param {File} file - PDF file
 * @returns {Promise<string>} - Extracted text
 */
export const extractTextFromPDF = async (file) => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    let fullText = "";

    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();

      // Sort items by vertical position (y) then horizontal position (x)
      const items = textContent.items.sort((a, b) => {
        const yDiff = Math.abs(a.transform[5] - b.transform[5]);
        // If items are on roughly the same line (within 5 units)
        if (yDiff < 5) {
          return a.transform[4] - b.transform[4]; // Sort by x position
        }
        return b.transform[5] - a.transform[5]; // Sort by y position (top to bottom)
      });

      let currentY = null;
      let lineText = "";

      items.forEach((item) => {
        const y = item.transform[5];

        // If this item is on a new line (y position changed significantly)
        if (currentY !== null && Math.abs(y - currentY) > 5) {
          fullText += lineText.trim() + "\n";
          lineText = "";
        }

        lineText += item.str + " ";
        currentY = y;
      });

      // Add the last line of the page
      if (lineText.trim()) {
        fullText += lineText.trim() + "\n";
      }
    }

    return fullText;
  } catch (error) {
    throw new Error("Failed to extract text from PDF file");
  }
};

/**
 * Extract text from DOCX file
 * @param {File} file - DOCX file
 * @returns {Promise<string>} - Extracted text
 */
export const extractTextFromDOCX = async (file) => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    return result.value;
  } catch (error) {
    throw new Error("Failed to extract text from DOCX file");
  }
};

/**
 * Extract email from text
 * @param {string} text - Resume text
 * @returns {string|null} - Email address
 */
const extractEmail = (text) => {
  const emailRegex = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/gi;
  const matches = text.match(emailRegex);
  return matches ? matches[0] : null;
};

/**
 * Extract phone number from text
 * @param {string} text - Resume text
 * @returns {string|null} - Phone number
 */
const extractPhone = (text) => {
  // Match various phone formats including international
  const phoneRegex =
    /(\+?\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}|\+\d{10,15}/g;
  const matches = text.match(phoneRegex);
  if (matches && matches.length > 0) {
    // Clean up the phone number
    let phone = matches[0].replace(/[^\d+]/g, "");
    // Ensure it starts with +
    if (!phone.startsWith("+")) {
      phone = "+" + phone;
    }
    return phone;
  }
  return null;
};

/**
 * Extract name from text (assumes name is at the beginning)
 * @param {string} text - Resume text
 * @returns {Object} - {first_name, last_name}
 */
const extractName = (text) => {
  const lines = text.split("\n").filter((line) => line.trim().length > 0);
  if (lines.length === 0) return { first_name: "", last_name: "" };

  // First non-empty line is usually the name
  // But limit to reasonable name length (max 100 characters)
  const nameLine = lines[0].trim().substring(0, 100);

  // Remove common non-name patterns (emails, phones, URLs)
  const cleanedName = nameLine
    .replace(/[+\d][\d\s()-]+/g, "") // Remove phone numbers
    .replace(/[a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+/g, "") // Remove emails
    .replace(/https?:\/\/[^\s]+/g, "") // Remove URLs
    .trim();

  // Split by spaces and take only first few words (max 5 words for a name)
  const nameParts = cleanedName
    .split(/\s+/)
    .filter(
      (part) =>
        part.length > 0 &&
        part.length < 30 && // Individual name part shouldn't be too long
        /^[a-zA-Z][a-zA-Z\s.-]*$/.test(part) // Should contain only letters, spaces, dots, hyphens
    )
    .slice(0, 5); // Take max 5 words

  if (nameParts.length >= 2) {
    return {
      first_name: nameParts[0],
      last_name: nameParts.slice(1).join(" "),
    };
  } else if (nameParts.length === 1) {
    return {
      first_name: nameParts[0],
      last_name: "",
    };
  }

  return { first_name: "", last_name: "" };
};

/**
 * Extract professional summary/about section
 * @param {string} text - Resume text
 * @returns {string} - Professional summary
 */
const extractSummary = (text) => {
  const summaryKeywords = [
    "summary",
    "profile",
    "objective",
    "about",
    "professional summary",
    "career objective",
    "professional profile",
  ];

  const lines = text.split("\n");
  let summaryText = "";
  let capturing = false;
  let captureCount = 0;

  // First, try to find summary with explicit header
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim().toLowerCase();

    // Check if this line is a summary header
    if (
      summaryKeywords.some(
        (keyword) => line === keyword || line.startsWith(keyword)
      )
    ) {
      capturing = true;
      continue;
    }

    // If we're capturing and hit another section header, stop
    if (capturing) {
      const sectionHeaders = [
        "experience",
        "education",
        "skills",
        "employment",
        "work history",
        "projects",
        "research",
        "publications",
      ];
      if (
        sectionHeaders.some(
          (header) => line === header || line.startsWith(header)
        )
      ) {
        break;
      }

      if (lines[i].trim().length > 0) {
        summaryText += lines[i].trim() + " ";
        captureCount++;

        // Limit to reasonable summary length (about 5-10 lines)
        if (captureCount >= 10) break;
      }
    }
  }

  // If we found a summary with header, return it
  if (summaryText.trim().length > 50) {
    return summaryText.trim().substring(0, 1200);
  }

  // Otherwise, try to extract summary without explicit header
  // Look for paragraph text after contact info and before first major section
  let foundContactEnd = false;
  let paragraphLines = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    const lineLower = line.toLowerCase();

    // Skip empty lines
    if (line.length === 0) continue;

    // Skip name line (usually first non-empty line, short)
    if (i < 3 && line.split(/\s+/).length <= 5) continue;

    // Skip contact info lines (email, phone, LinkedIn, GitHub, etc.)
    if (
      /@/.test(line) || // Email
      /\+?\d{1,3}[-.\s]?\(?\d{1,4}\)?[-.\s]?\d{1,4}[-.\s]?\d{1,9}/.test(line) || // Phone
      /linkedin|github|portfolio|website/i.test(line) || // Social links
      /https?:\/\//.test(line) // URLs
    ) {
      foundContactEnd = true;
      continue;
    }

    // If we've passed contact info, start capturing meaningful paragraphs
    if (foundContactEnd || i > 5) {
      // Stop at major section headers
      const majorSections = [
        "experience",
        "education",
        "skills",
        "employment",
        "work history",
        "projects",
        "research",
        "publications",
      ];

      if (
        majorSections.some(
          (section) =>
            lineLower === section ||
            lineLower.startsWith(section) ||
            lineLower.endsWith(section)
        )
      ) {
        break;
      }

      // If line is substantial (more than 20 characters), it's likely summary content
      if (line.length > 20) {
        paragraphLines.push(line);

        // Stop after collecting enough content (max 8 lines or 600 chars)
        if (
          paragraphLines.length >= 8 ||
          paragraphLines.join(" ").length > 600
        ) {
          break;
        }
      }
    }
  }

  // Return the collected paragraph as summary
  const extractedSummary = paragraphLines.join(" ").trim();
  return extractedSummary.substring(0, 1200);
};

/**
 * Extract skills from text
 * @param {string} text - Resume text
 * @returns {Array<string>} - List of skills
 */
const extractSkills = (text) => {
  const skillsKeywords = [
    "skills",
    "technical skills",
    "core competencies",
    "expertise",
  ];
  const lines = text.split("\n");
  let skills = [];
  let capturing = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim().toLowerCase();

    // Check if this line is a skills header
    if (
      skillsKeywords.some(
        (keyword) => line === keyword || line.startsWith(keyword)
      )
    ) {
      capturing = true;
      continue;
    }

    // If we're capturing and hit another section header, stop
    if (capturing) {
      const sectionHeaders = [
        "experience",
        "education",
        "employment",
        "work history",
        "projects",
        "certifications",
      ];
      if (
        sectionHeaders.some(
          (header) => line === header || line.startsWith(header)
        )
      ) {
        break;
      }

      if (lines[i].trim().length > 0) {
        // Split by common delimiters
        const lineSkills = lines[i]
          .split(/[,•|·-]/)
          .map((s) => s.trim())
          .filter((s) => s.length > 0 && s.length < 50);
        skills.push(...lineSkills);
      }

      if (skills.length >= 20) break; // Limit skills
    }
  }

  return skills.slice(0, 15); // Return max 15 skills
};

/**
 * Extract education information
 * @param {string} text - Resume text
 * @returns {Array<Object>} - List of education entries
 */
const extractEducation = (text) => {
  const educationKeywords = ["education", "academic", "qualification"];
  const degreeKeywords = [
    "bachelor",
    "master",
    "phd",
    "doctorate",
    "associate",
    "b.s",
    "m.s",
    "b.a",
    "m.a",
    "bsc",
    "msc",
  ];

  const lines = text.split("\n");
  let education = [];
  let capturing = false;
  let currentEntry = {};
  let entryLines = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim().toLowerCase();

    // Check if this line is an education header
    if (
      educationKeywords.some(
        (keyword) => line === keyword || line.startsWith(keyword)
      )
    ) {
      capturing = true;
      continue;
    }

    // If we're capturing
    if (capturing) {
      const sectionHeaders = [
        "experience",
        "employment",
        "work history",
        "projects",
        "certifications",
        "skills",
      ];
      if (
        sectionHeaders.some(
          (header) => line === header || line.startsWith(header)
        )
      ) {
        break;
      }

      if (lines[i].trim().length > 0) {
        entryLines.push(lines[i].trim());

        // Look for degree keywords
        if (degreeKeywords.some((deg) => line.includes(deg))) {
          currentEntry.degree = lines[i].trim();
        }

        // Look for years (graduation date)
        const yearMatch = lines[i].match(/\b(19|20)\d{2}\b/);
        if (yearMatch) {
          currentEntry.year = yearMatch[0];
        }

        // If we have enough info for an entry, save it
        if (currentEntry.degree && entryLines.length >= 2) {
          currentEntry.institution =
            entryLines.find((l) => l !== currentEntry.degree) || "";
          education.push({ ...currentEntry });
          currentEntry = {};
          entryLines = [];
        }
      }

      if (education.length >= 3) break; // Limit education entries
    }
  }

  return education;
};

/**
 * Extract work experience information
 * @param {string} text - Resume text
 * @returns {Array<Object>} - List of experience entries
 */
const extractExperience = (text) => {
  const experienceKeywords = [
    "experience",
    "employment",
    "work history",
    "professional experience",
  ];

  const lines = text.split("\n");
  let experience = [];
  let capturing = false;
  let currentEntry = {};
  let entryLines = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim().toLowerCase();

    // Check if this line is an experience header
    if (
      experienceKeywords.some(
        (keyword) => line === keyword || line.startsWith(keyword)
      )
    ) {
      capturing = true;
      continue;
    }

    // If we're capturing
    if (capturing) {
      const sectionHeaders = [
        "education",
        "skills",
        "projects",
        "certifications",
      ];
      if (
        sectionHeaders.some(
          (header) => line === header || line.startsWith(header)
        )
      ) {
        break;
      }

      if (lines[i].trim().length > 0) {
        entryLines.push(lines[i].trim());

        // Look for date patterns (e.g., "2020 - 2023", "Jan 2020 - Present")
        const dateMatch = lines[i].match(
          /(\d{4}|(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\s+\d{4})\s*[-–]\s*(\d{4}|present|current|(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\s+\d{4})/i
        );
        if (dateMatch) {
          currentEntry.dates = lines[i].trim();
        }

        // Accumulate entry data
        if (entryLines.length >= 3 && currentEntry.dates) {
          currentEntry.title = entryLines[0];
          currentEntry.company = entryLines[1];
          currentEntry.description = entryLines
            .slice(2)
            .join(" ")
            .substring(0, 500);

          experience.push({ ...currentEntry });
          currentEntry = {};
          entryLines = [];
        }
      }

      if (experience.length >= 5) break; // Limit experience entries
    }
  }

  return experience;
};

/**
 * Main resume parser function
 * @param {File} file - Resume file (PDF or DOCX)
 * @returns {Promise<Object>} - Parsed resume data
 */
export const parseResume = async (file) => {
  let text = "";

  // Extract text based on file type
  const fileType = file.type;
  if (fileType === "application/pdf") {
    text = await extractTextFromPDF(file);
  } else if (
    fileType ===
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  ) {
    text = await extractTextFromDOCX(file);
  } else {
    throw new Error(
      "Unsupported file format. Please upload a PDF or DOCX file."
    );
  }

  // Extract all information
  const { first_name, last_name } = extractName(text);
  const email = extractEmail(text);
  const phone = extractPhone(text);
  const summary = extractSummary(text);
  const skills = extractSkills(text);
  const education = extractEducation(text);
  const experience = extractExperience(text);

  return {
    first_name,
    last_name,
    email,
    phone,
    about: summary,
    skills,
    education,
    experience,
  };
};

export default parseResume;
